import React, { useRef, useEffect, useState } from 'react'
import { Printer, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import type { SalaryById } from '@/api/types/payroll.types'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function formatMonthYearLabel(ym?: string | null): string {
  if (!ym || ym.length < 6) return '—'
  const parts = String(ym).split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) return `${MONTH_NAMES[monthNum - 1]} ${yearStr}`
  return ym
}

function formatNum(n?: number | null): string {
  if (n == null) return '0.00'
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatJoiningDate(d?: string | null): string {
  if (!d) return '—'
  try {
    const date = new Date(d)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return String(d).slice(0, 10)
  }
}

/** Simple number to words for amount in SAR (e.g. "Three Thousand Nine Hundred Thirty") */
function amountInWords(num: number): string {
  if (num <= 0 || !Number.isFinite(num)) return 'Zero'
  const intPart = Math.floor(num)
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']

  function toWords(n: number): string {
    if (n === 0) return ''
    if (n < 10) return ones[n]
    if (n < 20) return teens[n - 10]
    if (n < 100) return (tens[Math.floor(n / 10)] + ' ' + ones[n % 10]).trim()
    if (n < 1000) return (ones[Math.floor(n / 100)] + ' Hundred ' + toWords(n % 100)).trim()
    if (n < 1000000) return (toWords(Math.floor(n / 1000)) + ' Thousand ' + toWords(n % 1000)).trim()
    if (n < 1000000000) return (toWords(Math.floor(n / 1000000)) + ' Million ' + toWords(n % 1000000)).trim()
    return (toWords(Math.floor(n / 1000000000)) + ' Billion ' + toWords(n % 1000000000)).trim()
  }

  const str = toWords(intPart).trim() || 'Zero'
  return str + ' in Saudi Riyals'
}

const TABLE_HEADER_CLASS = 'bg-primary text-primary-foreground text-left py-2 px-3 text-sm font-medium'
const TABLE_CELL_CLASS = 'border border-gray-200 py-2 px-3 text-sm text-gray-800'

export interface PaySlipModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: SalaryById | null
  loading?: boolean
  onPrint?: () => void
}

export interface EnrichedEmpInfo {
  employeeName?: string
  departmentName?: string
  nationalityName?: string
  joiningDate?: string
  reference?: string
  project?: string
}

export const PaySlipModal: React.FC<PaySlipModalProps> = ({ open, onOpenChange, data, loading, onPrint }) => {
  const printRef = useRef<HTMLDivElement>(null)
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const branchName = user?.selectedBranch?.branchName ?? 'Branch'
  const [empInfo, setEmpInfo] = useState<EnrichedEmpInfo | null>(null)

  useEffect(() => {
    if (!open || !data?.empId || !branchId) {
      setEmpInfo(null)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const [employee, departments, countries] = await Promise.all([
          employeesService.getEmployeeById(branchId, data.empId!),
          employeesService.getDepartmentLookups(),
          employeesService.getCountryLookups(),
        ])
        if (cancelled) return
        const firstName = employee.empFirstNameEn ?? ''
        const secondName = employee.empSecondNameEn ?? ''
        const lastName = employee.empLastNameEn ?? ''
        const employeeName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || undefined
        const deptId = employee.empDepartmetId ?? (employee as any).empDepartmentId
        const departmentName = deptId ? (departments.find((d) => d.id === deptId)?.lookupNameEn ?? (employee as any).departmentName) : undefined
        const nationalityId = employee.empNationalityId
        const nationalityName = nationalityId ? (countries.find((c) => c.id === nationalityId)?.lookupNameEn ?? (employee as any).nationalityName) : undefined
        setEmpInfo({
          employeeName: employeeName ?? (data as any).employeeName,
          departmentName: departmentName ?? (data as any).department,
          nationalityName: nationalityName ?? (data as any).nationality,
          joiningDate: employee.joiningDate ?? (data as any).joiningDate,
          reference: employee.empRefrence ?? (employee as any).empReferenceId ?? (data as any).reference,
          project: (data as any).project ?? (employee as any).projectName,
        })
      } catch {
        if (!cancelled) setEmpInfo(null)
      }
    }
    run()
    return () => { cancelled = true }
  }, [open, data?.empId, branchId])

  const handlePrint = () => {
    if (onPrint) {
      onPrint()
      return
    }
    const content = printRef.current
    if (!content) return
    const win = window.open('', '_blank')
    if (!win) {
      window.print()
      return
    }
    win.document.write(`
      <!DOCTYPE html><html><head>
        <title>Pay Slip</title>
        <link rel="stylesheet" href="${window.location.origin}/index.css" />
        <style>
          :root { --primary: 225 46% 16%; --primary-foreground: 0 0% 100%; }
          body { font-family: system-ui, sans-serif; padding: 16px; color: #1f2937; margin: 0; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; width: 100%; }
          th { background: hsl(var(--primary)); color: white; text-align: left; padding: 8px 12px; font-size: 14px; }
          td { border: 1px solid #e5e7eb; padding: 8px 12px; font-size: 14px; }
          .bg-primary { background: hsl(var(--primary)) !important; color: white !important; }
          .pay-slip-signature-footer.hidden { display: block !important; }
          .pay-slip-header { display: block !important; page-break-after: avoid !important; }
          .pay-slip-header-row { display: flex !important; justify-content: space-between !important; align-items: center !important; width: 100% !important; margin-bottom: 12px !important; }
          .pay-slip-header .pay-slip-date { flex-shrink: 0 !important; font-size: 12px !important; color: #6b7280 !important; }
          .pay-slip-header .pay-slip-branch { flex: 1 !important; text-align: center !important; font-size: 14px !important; font-weight: 600 !important; color: #1f2937 !important; }
          .pay-slip-header .pay-slip-logo-wrap { flex-shrink: 0 !important; display: flex !important; justify-content: flex-end !important; }
          .pay-slip-header .pay-slip-logo-wrap img { height: 32px !important; max-height: 32px !important; object-fit: contain !important; }
          .pay-slip-header .pay-slip-title { text-align: center !important; font-size: 20px !important; font-weight: 600 !important; color: #111827 !important; margin: 8px 0 !important; }
          .pay-slip-header .pay-slip-line { height: 3px !important; width: 100% !important; background: hsl(var(--primary)) !important; margin: 0 0 24px 0 !important; }
          .pay-slip-signature-footer { display: block !important; margin-top: auto !important; page-break-inside: avoid !important; }
          .pay-slip-signature-footer .pay-slip-sig-row { display: flex !important; gap: 48px !important; margin: 32px 0 24px !important; }
          .pay-slip-signature-footer .pay-slip-sig-item { flex: 1 !important; }
          .pay-slip-signature-footer .pay-slip-company { text-align: center !important; font-size: 12px !important; color: #4b5563 !important; padding-top: 16px !important; border-top: 1px solid #e5e7eb !important; }
          .pay-slip-deduction-table { margin-bottom: 0 !important; }
          .pay-slip-net-card { display: block !important; margin-top: 16px !important; margin-bottom: 0 !important; page-break-before: avoid !important; border: 1px solid #e5e7eb !important; }
          .pay-slip-net-card .bg-primary { background: hsl(var(--primary)) !important; color: white !important; }
          @media print {
            @page { size: A4; margin: 10mm; }
            html, body { margin: 0 !important; padding: 0 !important; height: 100% !important; overflow: hidden !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .pay-slip-print { page-break-inside: avoid !important; break-inside: avoid !important; transform: scale(0.82); transform-origin: top left; width: 122%; min-height: 0; display: flex !important; flex-direction: column !important; min-height: 100vh !important; }
            .pay-slip-signature-footer { display: block !important; margin-top: auto !important; }
          }
        </style>
      </head><body>
        <div class="pay-slip-print">${content.innerHTML}</div>
      </body></html>
    `)
    win.document.close()
    win.focus()
    setTimeout(() => {
      win.print()
      win.close()
    }, 250)
  }

  const showContent = !loading && data

  const monthYearLabel = data ? formatMonthYearLabel(data.salaryMonthYear) : ''
  const basic = data?.basicSalary ?? 0
  const housing = data?.housingAllowance ?? 0
  const transport = data?.transpotationAllowance ?? 0
  const meal = data?.mealAllowance ?? 0
  const otherAdditions = (data?.otherAllowance ?? 0) + (data?.monthlyBonus ?? 0) + (data?.monthlyIncentive ?? 0)
  const gosi = data?.gosiDeduction ?? 0
  const absentDed = data?.absentDeduction ?? 0
  const otherDed = (data?.otherDeductions ?? 0) + (data?.loanDeduction ?? 0) + (data?.nonRecurriveDeduction ?? 0)
  const totalDeduction = data?.totalDeduction ?? (gosi + absentDed + otherDed)
  const netSalary = data?.totalSalary ?? (basic + housing + transport + meal + otherAdditions - totalDeduction)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="bg-primary text-primary-foreground px-6 py-4 rounded-t-lg shrink-0 no-print">
          <DialogTitle className="text-white">Details | Salary Details</DialogTitle>
        </DialogHeader>

        <div ref={printRef} className="p-6 bg-white text-gray-900 print:block min-h-[200px] pay-slip-print">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          )}
          {!showContent && !loading && (
            <div className="py-16 text-center text-muted-foreground">No data to display.</div>
          )}
          {showContent && (
            <React.Fragment>
              {/* Header: same in modal and print — top-left date, center branch, right small logo; then payslip month; then header line */}
              <div className="pay-slip-header flex flex-col w-full">
                <div className="pay-slip-header-row flex justify-between items-center gap-4 mb-3 w-full">
                  <div className="pay-slip-date flex-shrink-0 text-left text-xs text-muted-foreground">
                    {new Date().toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' })} {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                  <div className="pay-slip-branch flex-1 flex justify-center min-w-0">
                    <span className="text-sm font-semibold text-gray-800">{branchName}</span>
                  </div>
                  <div className="pay-slip-logo-wrap flex-shrink-0 flex justify-end">
                    <img src={getLogoPath()} alt="Logo" className="h-8 w-auto object-contain print:h-7" style={{ maxHeight: 32 }} />
                  </div>
                </div>
                <h2 className="pay-slip-title text-xl font-semibold text-center text-gray-900 mb-2">
                  Pay Slip for the month of {monthYearLabel}
                </h2>
                <div className="pay-slip-line h-0.5 bg-primary w-full mb-6" style={{ minHeight: 3 }} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Employee Information */}
                <div>
                  <table className="w-full border border-gray-200">
                <thead>
                  <tr>
                    <th colSpan={2} className={TABLE_HEADER_CLASS}>Employee Information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>Project</td><td className={TABLE_CELL_CLASS}>{empInfo?.project ?? data.project ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Department</td><td className={TABLE_CELL_CLASS}>{empInfo?.departmentName ?? data.department ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Employee #</td><td className={TABLE_CELL_CLASS}>{data.empCode ?? `EMP-${data.empId ?? '—'}`}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Employee Name</td><td className={TABLE_CELL_CLASS}>{empInfo?.employeeName ?? data.employeeName ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Reference</td><td className={TABLE_CELL_CLASS}>{empInfo?.reference ?? data.reference ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Joining Date</td><td className={TABLE_CELL_CLASS}>{formatJoiningDate(empInfo?.joiningDate ?? data.joiningDate)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Nationality</td><td className={TABLE_CELL_CLASS}>{empInfo?.nationalityName ?? data.nationality ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Contract Salary</td><td className={TABLE_CELL_CLASS}>{formatNum(data.contractSalary)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Pay Days</td><td className={TABLE_CELL_CLASS}>{data.payDays ?? '30'}</td></tr>
                </tbody>
                  </table>
                </div>

                {/* Earning Elements */}
                <div>
                  <table className="w-full border border-gray-200">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CLASS}>Earning Elements</th>
                    <th className={TABLE_HEADER_CLASS}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>Basic</td><td className={TABLE_CELL_CLASS}>{formatNum(basic)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Housing</td><td className={TABLE_CELL_CLASS}>{formatNum(housing)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Transportation</td><td className={TABLE_CELL_CLASS}>{formatNum(transport)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Food</td><td className={TABLE_CELL_CLASS}>{formatNum(meal)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Other Additions</td><td className={TABLE_CELL_CLASS}>{formatNum(otherAdditions)} (SAR)</td></tr>
                </tbody>
              </table>

              <table className="w-full border border-gray-200 mt-4 pay-slip-deduction-table">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CLASS}>Deduction Elements</th>
                    <th className={TABLE_HEADER_CLASS}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>GOSI</td><td className={TABLE_CELL_CLASS}>{formatNum(gosi)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Absence Deduction</td><td className={TABLE_CELL_CLASS}>{formatNum(absentDed)} (SAR)</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Other Deductions</td><td className={TABLE_CELL_CLASS}>{formatNum(otherDed)} (SAR)</td></tr>
                  <tr className="bg-primary text-primary-foreground"><td className="py-2 px-3 font-medium">Total Deduction</td><td className="py-2 px-3 font-medium">{formatNum(totalDeduction)} (SAR)</td></tr>
                </tbody>
              </table>

              <div className="pay-slip-net-card mt-4 border border-gray-200 block">
                <div className="bg-primary text-primary-foreground py-2 px-3 font-medium">Net Payable Salary</div>
                <div className="py-2 px-3 text-lg font-semibold text-gray-900">{formatNum(netSalary)} (SAR)</div>
                <div className="py-2 px-3 text-sm text-gray-700 border-t border-gray-200">Amount in Word: {amountInWords(netSalary)}</div>
              </div>
                </div>
              </div>

              {/* Signatures + Footer: hidden in modal, same layout in print */}
              <div className="pay-slip-signature-footer hidden">
                <div className="pay-slip-sig-row flex gap-12 mt-8 mb-6">
                  <div className="pay-slip-sig-item flex-1">
                    <div className="text-sm font-medium text-gray-700">Prepared by</div>
                    <div className="mt-1 min-h-[28px] border-b border-dashed border-gray-500" style={{ borderWidth: '1px' }} />
                  </div>
                  <div className="pay-slip-sig-item flex-1">
                    <div className="text-sm font-medium text-gray-700">Received by</div>
                    <div className="mt-1 min-h-[28px] border-b border-dashed border-gray-500" style={{ borderWidth: '1px' }} />
                  </div>
                </div>
                <div className="pay-slip-company text-center text-sm text-gray-600 border-t border-gray-200 pt-4">
                  S AND K Aerospace LLC Company - Tel: 9660555340017 C.R.: 1010315389
                </div>
              </div>
            </React.Fragment>
          )}
        </div>

        <DialogFooter className="border-t p-4 bg-gray-50 rounded-b-lg shrink-0 no-print">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} disabled={!data}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
